import React, {useEffect, useRef, useState} from "react";
import {Alert, Badge, Button, Col, Modal, Row, Space, Spin, Tooltip} from "antd";
import {AgGridReact} from "ag-grid-react";
import type {GridApi} from "ag-grid-community";
import {DownloadOutlined} from "@ant-design/icons";
import type {IntlShape} from "react-intl";
import {FailureDownloadDocsResp} from "../../../components";
import client from "../../../config/GraphQLApolloClient";
import {getI18n} from "../../../utils/Utils";
import {getAgGridLocalization, PersonalizeGridView} from "../../info-portal/components/grid/GridUtil";
import moduleStyles from "./document.module.css";
import RenderIfEntitled from "../../../components/EntitlementComponents";
import MyDocumentSearchForm from "./myDocumentSearchForm";
import {DOWNLOAD_MULTIPLE_DOCUMENT} from "../queries";
import {UPDATE_MULTIPLE_DOWNLOAD_STATUS} from "../mutations";
import {showErrorNotification} from "../../Notification";
import {getDocumentsData} from "../DocumentService";
import {base64toZipFile, MYDOCS_TABLE_HEADERS} from "../DocumentUtils";
import {useFreightGridToolbar} from "./hooks/useFreightGridToolbar";
import FreightGridToolbar from "./components/FreightGridToolbar";
import FreightWorkspaceShell from "./components/FreightWorkspaceShell";
import dayjs from "dayjs";

interface MyDocumentsFreightTabProps {
    intl: IntlShape;
    currentUser: any;
    distinctEntitlements: string[];
}

const MyDocumentsFreightTab: React.FC<MyDocumentsFreightTabProps> = ({
    intl,
    currentUser,
    distinctEntitlements,
}) => {
    const [loading, setLoading] = useState(false);
    const [docGridApi, setDocGridApi] = useState<GridApi>(undefined);
    const [docData, setDocData] = useState(undefined);
    const [docCount, setDocCount] = useState(0);
    const [formDataValues, setFormDataValues] = useState(undefined);
    const [failureDocuments, setFailureDocuments] = useState<FailureDownloadDocsResp[]>(undefined);
    const gridView = useRef<PersonalizeGridView>(
        new PersonalizeGridView(
            "freight-document_ag_grid_personalized_view",
            MYDOCS_TABLE_HEADERS(intl, currentUser),
        ),
    );

    useEffect(() => {
        setLoading(false);
        setDocCount(0);
        setDocData(null);
        setFailureDocuments(undefined);
    }, [currentUser]);

    const { menuItems, onExport, onGridReady, onResetView } = useFreightGridToolbar({
        columnDefs: gridView.current.getView(),
        exportWarningTitle: getI18n("Warning-export-message", "Export Document(s)", intl),
        intl,
        storageKey: "freight-document_ag_grid_personalized_view",
    });

    const onDocGridReady = (params) => {
        setDocGridApi(params.api);
        onGridReady(params);
    };

    const onSearch = (values) => {
        setLoading(true);
        setFormDataValues(values);

        const documentInput = {
            fromDate: values.fromDate ? dayjs(values.fromDate.startOf("d")) : undefined,
            toDate: values.dateTo ? dayjs(values.dateTo.endOf("d")) : undefined,
            companyCode: values.companyCode,
            reference: values.reference ? values.reference.trim() : undefined,
            partnerno: values.partnerNo ? values.partnerNo.trim() : undefined,
            downloaded: values.downloaded,
            orderArea: values.orderArea,
        };

        localStorage.setItem("documentInput", JSON.stringify(documentInput));
        Promise.resolve(getDocumentsData(currentUser.username, documentInput))
            .then((data) => {
                setDocCount(data.getPartnerDocuments.length);
                setDocData(data.getPartnerDocuments);
            })
            .catch((err) => {
                setLoading(false);
                console.error(err);
            })
            .finally(() => setLoading(false));
    };

    const downloadDocuments = () => {
        setLoading(true);
        const documentDownloadInputList = [];
        const selectedRows = docGridApi.getSelectedRows();

        if (selectedRows?.length) {
            for (const document of selectedRows) {
                documentDownloadInputList.push({
                    blobId: document.documentId,
                    documentName: document.azDownloadFileName,
                    documentPath: document.azItemPathName,
                    documentLink: document.downloadLink,
                });
                console.log(documentDownloadInputList);
            }
            client
                .query({
                    query: DOWNLOAD_MULTIPLE_DOCUMENT,
                    variables: {
                        documentDownloadInputs: documentDownloadInputList,
                    },
                    fetchPolicy: "network-only",
                })
                .then((response) => {
                    base64toZipFile(response.data.downloadMultipleDocument.encodedZipFileData, new Date().toISOString().substring(0, 10).concat("-download.zip"));
                    setFailureDocuments(response.data.downloadMultipleDocument.failureDocuments);
                    setLoading(false);
                })
                .then(() => {
                    client.mutate({
                        mutation: UPDATE_MULTIPLE_DOWNLOAD_STATUS,
                        variables: {documentDownloadInputs: documentDownloadInputList},
                    }).then((_) => {
                        onSearch(formDataValues);
                    }).catch((e) => {
                        console.error(e);
                    });
                })
                .catch((e) => {
                    console.error(e);
                    setLoading(false);
                    showErrorNotification(`${e}`);
                });
        }
        else {
            Modal.warning({
                title: getI18n(
                    "Warning-message",
                    "Export Document(s)", intl,
                ),
                content: getI18n(
                    "Warning-export-message",
                    "You have not selected any items to be exported. Please select an item first.", intl,
                ),
            });
        }
    };

    return (
        <Spin spinning={loading} size={"large"}>
            {failureDocuments &&
                <Row gutter={24}>
                    <Col span={24} className="mb5 mt5">
                        {failureDocuments.map((document) => (
                            <Alert
                                key={document.documentId}
                                message={`${document.errorMessage}`}
                                type="error"
                                showIcon={true}
                                closable={true}
                            />
                        ))}
                    </Col>
                </Row>}

            <FreightWorkspaceShell
                searchForm={
                    <MyDocumentSearchForm
                        onSearch={onSearch}
                        currentUser={currentUser}
                        intl={intl}
                        distinctEntitlements={distinctEntitlements}
                    />
                }
                resultHeader={
                    <div className={moduleStyles.freightResultMeta}>
                        <span className={moduleStyles.freightResultCount}>
                            {getI18n("welcome-No_documents", "Number Of Documents", intl)}
                        </span>
                        <Space>
                            <Badge
                                className="site-badge-count-109"
                                count={docCount}
                                style={{backgroundColor: "#52c41a"}}
                                title={"Number of Documents"}
                                overflowCount={999999}
                            />
                        </Space>
                    </div>
                }
                toolbar={
                    <FreightGridToolbar
                        intl={intl}
                        menuItems={menuItems}
                        onExport={onExport}
                        onResetView={onResetView}
                        extraActions={
                            <RenderIfEntitled
                                entitlementToCheck="download@freight"
                                intl={intl}
                                distinctEntitlements={distinctEntitlements}
                            >
                                <div className={moduleStyles.documentsToolbarCol}>
                                    <Tooltip title={intl.formatMessage({id: "welcome-download-button"})}>
                                        <Button
                                            data-testid={"result-download"}
                                            onClick={() => downloadDocuments()}
                                            icon={<DownloadOutlined />}
                                            size={"middle"}
                                        >
                                            {intl.formatMessage({id: "welcome-download-button"})}
                                        </Button>
                                    </Tooltip>
                                </div>
                            </RenderIfEntitled>
                        }
                    />
                }
                grid={
                    <div className={moduleStyles.documentsGridContainer}>
                        <AgGridReact
                            defaultColDef={{
                                enableValue: true,
                                sortable: true,
                                resizable: true,
                            }}
                            columnDefs={gridView.current.getView()}
                            rowData={docData}
                            sideBar={false}
                            rowClass="agGridRows"
                            suppressMenuHide={true}
                            debug={true}
                            onGridSizeChanged={() => docGridApi?.sizeColumnsToFit()}
                            onGridReady={onDocGridReady}
                            rowSelection={"multiple"}
                            pagination={true}
                            localeText={getAgGridLocalization(intl)}
                            paginationPageSize={20}
                            suppressRowClickSelection={true}
                            enableBrowserTooltips={true}
                            data-testid="documents-test-id"
                        />
                    </div>
                }
            />
        </Spin>
    );
};

export default MyDocumentsFreightTab;
